import React, { useState, useMemo, useRef, useEffect } from 'react';
import { thumbUrl } from './metaApiSource';
import { AD_PREFIX_RE } from '../creatorType';

const euros = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  if (n >= 1000) return '€' + (n/1000).toFixed(1).replace('.0','') + 'k';
  return '€' + Math.round(n);
};

function daysLive(ad) {
  if (!ad.first_date) return null;
  const start = new Date(ad.first_date);
  const end = ad.last_date ? new Date(ad.last_date) : new Date();
  const days = Math.max(0, Math.round((end - start) / 86400000));
  return days;
}

function shortCreator(ad) {
  const raw = ad.nomenclature?.creator || (ad.ad_name_sample || '').split('_')[0] || '—';
  return raw.replace(/_/g, ' ');
}

function shortConcept(ad) {
  const nom = ad.nomenclature || {};
  const parts = [];
  if (nom.feature) parts.push(nom.feature);
  if (nom.lang && nom.lang !== 'any') parts.push(nom.lang);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

const SORT_FIELDS = {
  spend: (a) => a.spend || 0,
  purchases: (a) => a.achats || 0,
  cpa: (a) => (a.cpa === null || a.cpa === undefined) ? Infinity : a.cpa,
  days_live: (a) => daysLive(a) ?? -1,
};

const TOP_N_OPTIONS = [10, 20, 30, 50, 'All'];

// v3.99.6 Fix 6 — local date picker presets for Main adset (independent of global tab range)
export const MAIN_LOCAL_PRESETS = [
  { value: 'same', label: 'Same as global' },
  { value: '7d', label: 'Last 7 days' },
  { value: '10d', label: 'Last 10 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
];

function presetToRange(preset, globalRange) {
  if (preset === 'same') return globalRange;
  const today = new Date();
  const until = today.toISOString().slice(0, 10);
  const daysMap = { '7d': 7, '10d': 10, '14d': 14, '30d': 30 };
  const days = daysMap[preset] || 10;
  const since = new Date(today);
  since.setDate(since.getDate() - (days - 1));
  return { since: since.toISOString().slice(0, 10), until };
}

function formatLocalLabel(preset) {
  if (preset === 'same') return 'Same as global';
  const found = MAIN_LOCAL_PRESETS.find(p => p.value === preset);
  return found ? found.label : 'Last 10 days';
}

// Standalone date picker so the Main assets block header (in PerformanceTab)
// can carry it on the right, matching the Overview header pattern.
export function MainDatePicker({ preset, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  return (
    <div className="perf-main-grid-localdate" ref={ref}>
      <button
        type="button"
        className={'perf-hctrl' + (open ? ' open' : '')}
        onClick={() => setOpen(v => !v)}>
        <span aria-hidden="true">📅</span>
        {formatLocalLabel(preset)}
        <span className="perf-hctrl-chev" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="perf-main-grid-popover">
          {MAIN_LOCAL_PRESETS.map(p => (
            <button
              key={p.value}
              type="button"
              className={'perf-main-grid-preset' + (preset === p.value ? ' active' : '')}
              onClick={() => { onChange(p.value); setOpen(false); }}>
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Phase filter — mirror kpiCalculator.phaseFromCategory permissive logic so the local
// filter does NOT depend on the upstream phase computation (which may not run before
// the data hits the grid in some edge cases).
function isMainPhase(ad) {
  const c = (ad.adset_category || '').toUpperCase();
  if (['MAIN', 'BIS', 'ITERATION', 'M'].includes(c)) return true;
  return c.includes('MAIN') || c.includes('BIS') || c.includes('ITER');
}

function SortHeader({ label, field, sortBy, sortDir, onSort, align, compact }) {
  const isActive = sortBy === field;
  const arrow = isActive ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';
  const baseClass = compact ? 'perf-main-th compact' : 'perf-main-th';
  return (
    <button
      type="button"
      className={baseClass + (isActive ? ' active' : '') + (align ? ' align-' + align : '')}
      onClick={() => onSort(field)}>
      {label}{arrow}
    </button>
  );
}

export default function MainAdsetGrid({ allInfluencerAds, globalDateRange, localPreset = '10d', threshold, viewMode = 'grid', activeFilter, onOpenCreative, onPlay }) {
  const [sortBy, setSortBy] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [shown, setShown] = useState(10);

  const localRange = useMemo(
    () => presetToRange(localPreset, globalDateRange),
    [localPreset, globalDateRange]
  );

  // Filter to main-phase + local date window
  const mainAdsInWindow = useMemo(() => {
    const arr = (allInfluencerAds || []).filter(ad => {
      if (!isMainPhase(ad)) return false;
      if (!localRange || !localRange.since || !localRange.until) return true;
      // v4.00 — overlap sur la période réelle ; aucune date => exclue de la vue fenêtrée.
      if (!ad.last_date && !ad.first_date) return false;
      const adStart = ad.first_date || ad.last_date;
      const adEnd = ad.last_date || ad.first_date;
      return adStart <= localRange.until && adEnd >= localRange.since;
    });
    return arr;
  }, [allInfluencerAds, localRange]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir(field === 'cpa' ? 'asc' : 'desc'); }
  };

  const filtered = useMemo(() => {
    let arr = mainAdsInWindow;
    if (activeFilter) arr = arr.filter(c => c.action === activeFilter);
    const getter = SORT_FIELDS[sortBy] || SORT_FIELDS.spend;
    arr = [...arr].sort((a, b) => {
      const va = getter(a); const vb = getter(b);
      return sortDir === 'desc' ? (vb - va) : (va - vb);
    });
    return arr;
  }, [mainAdsInWindow, activeFilter, sortBy, sortDir]);

  const visible = filtered.slice(0, shown);

  const thr = threshold || { minSpend: 2000, maxCpa: 50 };
  const statusOf = (ad) => {
    const sp = ad.spend || 0, cp = ad.cpa;
    if (sp >= thr.minSpend && cp !== null && cp !== undefined && cp <= thr.maxCpa) return { label: 'Spending', cls: 'fnl-b-spend' };
    if (sp >= thr.minSpend * 0.4 && cp !== null && cp !== undefined && cp <= thr.maxCpa * 1.4) return { label: 'Scaling', cls: 'fnl-b-scale' };
    return { label: 'Underperforming', cls: 'fnl-b-under' };
  };

  return (
    <div className="perf-main-grid">
      {viewMode === 'list' && (
        <div className="perf-main-grid-header-unified">
          <div className="perf-main-grid-header-left"></div>
          <SortHeader label="Spend" field="spend" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" compact />
          <SortHeader label="Purch." field="purchases" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" compact />
          <SortHeader label="CPA" field="cpa" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" compact />
          <SortHeader label="Live" field="days_live" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" compact />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="perf-main-grid-empty">
          {activeFilter
            ? 'No ads matching ' + activeFilter + ' in Main adset.'
            : 'No ads in this period. Try widening the date range.'}
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(158px, 1fr))', gap: 12, padding: '12px 16px' }}>
          {visible.map(ad => {
            const t = thumbUrl(ad);
            const name = (ad.ad_name_sample || '').replace(AD_PREFIX_RE, '').split(/_HOOK|_DUR|_ugc/i)[0].replace(/_/g, ' ').slice(0, 26);
            const st = statusOf(ad);
            return (
              <div key={ad.core_id || ad.ad_name_sample} style={{ border: '1px solid #eee', borderRadius: 11, overflow: 'hidden', background: '#fff' }}>
                <div onClick={() => onPlay && ad.ad_id && onPlay(ad.ad_id)} style={{ position: 'relative', aspectRatio: '9 / 16', background: '#f0f0f0', cursor: ad.ad_id ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {t
                    ? <img src={t} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ color: '#bbb', fontSize: 26 }}>▶</span>}
                  {t && <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, pointerEvents: 'none' }}>▶</span>}
                </div>
                <div onClick={() => onOpenCreative && onOpenCreative(ad)} style={{ padding: '8px 10px', cursor: 'pointer' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ad.ad_name_sample}>{name || ('#' + ad.core_id)}</div>
                  <div style={{ marginTop: 5 }}><span className={'fnl-badge ' + st.cls} style={{ fontSize: 10 }}>{st.label}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11 }}>
                    <span><b>{ad.achats ?? 0}</b> <span style={{ color: '#999' }}>ach.</span></span>
                    <span><b>€{ad.cpa !== null && ad.cpa !== undefined ? Math.round(ad.cpa) : '—'}</b> <span style={{ color: '#999' }}>CPA</span></span>
                    <span><b>{euros(ad.spend)}</b></span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="perf-main-grid-tbody">
          {visible.map(ad => {
            const dl = daysLive(ad);
            const thumb = thumbUrl(ad);
            const st = statusOf(ad);
            return (
              <div
                key={ad.core_id || ad.ad_name_sample}
                className="perf-main-grid-row"
                onClick={() => onOpenCreative && onOpenCreative(ad)}>
                <div className="perf-main-td-thumb" onClick={(e) => { if (onPlay && ad.ad_id) { e.stopPropagation(); onPlay(ad.ad_id); } }} style={{ cursor: ad.ad_id ? 'pointer' : 'default' }}>
                  {thumb
                    ? <img src={thumb} alt="" loading="lazy" />
                    : <div className="perf-main-thumb-placeholder">▶</div>}
                </div>
                <div className="perf-main-td-ad">
                  <div className="perf-main-td-name" title={ad.ad_name_sample}>
                    {ad.ad_name_sample
                      ? ad.ad_name_sample.replace(AD_PREFIX_RE, '').slice(0, 40)
                      : '—'}
                  </div>
                  <div className="perf-main-td-id">#{ad.nomenclature?.metaSeqId || ad.core_id || '—'}</div>
                  <div style={{ marginTop: 3 }}><span className={'fnl-badge ' + st.cls} style={{ fontSize: 9.5, padding: '1px 7px' }}>{st.label}</span></div>
                </div>
                <div className="perf-main-td">{shortCreator(ad)}</div>
                <div className="perf-main-td">{shortConcept(ad)}</div>
                <div className="perf-main-td align-right">{euros(ad.spend)}</div>
                <div className="perf-main-td align-right">{ad.achats ?? 0}</div>
                <div className="perf-main-td align-right">
                  {ad.cpa !== null && ad.cpa !== undefined ? '€' + Math.round(ad.cpa) : '—'}
                </div>
                <div className="perf-main-td align-right">
                  {dl !== null ? dl + (dl === 1 ? ' day' : ' days') : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > 10 && (
        <div className="perf-main-grid-footer" style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '12px 16px' }}>
          {shown < filtered.length && (
            <button type="button" onClick={() => setShown(sn => sn + 10)}
              style={{ border: '1px dashed rgba(255,121,0,0.4)', background: '#fff', color: '#FF7900', borderRadius: 9, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              + Show 10 more ({filtered.length - shown} left)
            </button>
          )}
          {shown > 10 && (
            <button type="button" onClick={() => setShown(10)}
              style={{ border: '1px solid #e5e5e5', background: '#fff', color: '#999', borderRadius: 9, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
}
